<script lang="ts">
	import { FileUpload } from '@repo/commons/components'
	import FileList from './FileList.svelte'
	import { generateId } from '@repo/commons/utils'
	import type { FileWithState } from '@repo/commons/models'
	import { FileStatus } from '@repo/commons/models'

	const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
	const FINALIZATION_DELAY_MS = 500
	const DEFAULT_ALLOWED_FILE_TYPES = [
		'image/jpeg',
		'image/png',
		'application/pdf',
		'text/x-markdown',
		'text/markdown'
	]

	interface Props {
		maxSize?: number
		allowedTypes?: string[]
		showDropzone?: boolean
		multiple?: boolean
		onUpload: (
			file: File,
			fileMetadata: FileWithState
		) => Promise<{ success: boolean; data?: any; error?: string }>
		onError?: (error: string) => void
		onRemove?: () => void
		fileUploadState: {
			selectedFiles: FileWithState[]
			errorMessage: string
		}
		validateBeforeUpload?: (file: File) => Promise<{
			isValid: boolean
			errors?: { group: string; errors: string[] }[]
			warnings?: { group: string; warnings: string[] }[]
		}>
	}

	let {
		showDropzone = true,
		multiple = true,
		maxSize = DEFAULT_MAX_FILE_SIZE,
		allowedTypes = DEFAULT_ALLOWED_FILE_TYPES,
		onUpload,
		onError,
		onRemove,
		fileUploadState = $bindable({ selectedFiles: [], errorMessage: '' }),
		validateBeforeUpload
	}: Props = $props()

	function updateFile(id: string, patch: Partial<FileWithState>) {
		fileUploadState = {
			...fileUploadState,
			selectedFiles: fileUploadState.selectedFiles.map((f) =>
				f.id === id ? { ...f, ...patch } : f
			)
		}
	}

	async function uploadFile(fileId: string): Promise<void> {
		const fileItem = fileUploadState.selectedFiles.find((f) => f.id === fileId)
		if (!fileItem) return

		if (validateBeforeUpload) {
			const validation = await validateBeforeUpload(fileItem.file)
			if (!validation.isValid) {
				updateFile(fileId, {
					status: FileStatus.ERROR,
					errors: validation.errors ?? [],
					warnings: validation.warnings
				})
				return
			}
		}

		updateFile(fileId, { status: FileStatus.UPLOADING, errors: [], warnings: [] })

		try {
			const result = await onUpload(fileItem.file, fileItem)

			if (result.success) {
				updateFile(fileId, { status: FileStatus.FINALIZING })
				setTimeout(() => {
					// delayed finalization without closure staleness
					updateFile(fileId, {
						status: FileStatus.UPLOADED,
						uploadResult: result.data
					})
				}, FINALIZATION_DELAY_MS)
			} else {
				updateFile(fileId, { status: FileStatus.FINALIZING })
				setTimeout(() => {
					// delayed finalization without closure staleness
					updateFile(fileId, {
						status: FileStatus.ERROR,
						errors: [{ group: 'Upload failed', errors: [result.error ?? 'Upload failed'] }]
					})
				}, FINALIZATION_DELAY_MS)
			}
		} catch (error) {
			updateFile(fileId, {
				status: FileStatus.ERROR,
				errors: [
					{
						group: 'Upload failed',
						errors: [error instanceof Error ? error.message : 'Upload failed']
					}
				]
			})
		}
	}

	async function handleFileSelect(files: File[]) {
		const newFiles: FileWithState[] = files.map((file) => ({
			id: generateId(),
			file,
			status: FileStatus.PENDING,
			errors: [],
			warnings: []
		}))

		fileUploadState.selectedFiles = [...newFiles, ...fileUploadState.selectedFiles]
		fileUploadState.errorMessage = ''

		// Start uploading all new files immediately (in parallel)
		const uploadPromises = newFiles.map((fileItem) => uploadFile(fileItem.id))

		// Wait for all uploads to complete
		await Promise.allSettled(uploadPromises)
	}

	function handleError(error: string) {
		fileUploadState.errorMessage = error
		onError?.(error)
	}

	function removeFile(fileIndex: number) {
		fileUploadState.selectedFiles = fileUploadState.selectedFiles.filter(
			(_, index) => index !== fileIndex
		)
		// Call the onRemove callback if provided
		onRemove?.()
	}
</script>

<div>
	{#if showDropzone}
		<FileUpload
			{maxSize}
			{allowedTypes}
			{multiple}
			onFileSelect={handleFileSelect}
			onError={handleError}
		/>
	{/if}

	<div class="mt-6">
		<FileList
			items={fileUploadState.selectedFiles}
			onRemoveFile={removeFile}
			onRetryFile={uploadFile}
		/>
	</div>
</div>

<style>
</style>
